import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Star, X, MapPin } from "@phosphor-icons/react";
import { useSavedLocations } from "../lib/SavedLocationsContext";
import { geocode } from "../lib/proximity";
import {
  getPushEnabled,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
  syncSavedLocations,
} from "../lib/pushClient";
import {
  LocationSearchForm,
  GeocodeResults,
} from "./saved-locations/LocationSearchForm";
import SavedLocationItem from "./saved-locations/SavedLocationItem";
import {
  PushNotificationsPanel,
  SyncIndicator,
} from "./saved-locations/PushNotificationsPanel";

function parseCoords(q) {
  // Decimal pairs: "48.9059, -119.0113" or "48.9059 -119.0113"
  let m = q.match(/^\s*(-?\d{1,2}(?:\.\d+)?)[,\s]+(-?\d{1,3}(?:\.\d+)?)\s*$/);
  if (m) {
    const lat = parseFloat(m[1]);
    const lon = parseFloat(m[2]);
    if (Math.abs(lat) <= 90 && Math.abs(lon) <= 180) return { lat, lon };
  }
  // DMS: 48°54'21.2"N 119°00'40.7"W
  m = q.match(
    /(\d{1,3})[°\s]\s*(\d{1,2})['′]\s*([\d.]+)?["″]?\s*([NS])[,\s]+(\d{1,3})[°\s]\s*(\d{1,2})['′]\s*([\d.]+)?["″]?\s*([EW])/i,
  );
  if (m) {
    let lat = +m[1] + +m[2] / 60 + (+m[3] || 0) / 3600;
    if (m[4].toUpperCase() === "S") lat = -lat;
    let lon = +m[5] + +m[6] / 60 + (+m[7] || 0) / 3600;
    if (m[8].toUpperCase() === "W") lon = -lon;
    return { lat, lon };
  }
  return null;
}

export default function SavedLocationsManager({ open, onClose }) {
  const { locations, add, remove, update, saveNow, syncState } = useSavedLocations();
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const inputRef = useRef(null);

  const handleSaveAll = useCallback(async () => {
    setSaveState("saving");
    try {
      if (getPushEnabled()) await syncSavedLocations(locations);
      const ok = await saveNow();
      setSaveState(ok ? "saved" : "error");
    } catch {
      setSaveState("error");
    }
    setTimeout(() => setSaveState("idle"), 2500);
  }, [locations, saveNow]);

  useEffect(() => {
    setPushEnabled(getPushEnabled());
  }, [open]);

  // Keep server in sync when locations change (only if push enabled)
  useEffect(() => {
    if (getPushEnabled()) {
      syncSavedLocations(locations);
    }
  }, [locations]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (!open) {
      setQuery("");
      setResults([]);
      setError(null);
    }
  }, [open]);

  const handleSearch = useCallback(
    async (e) => {
      e.preventDefault();
      if (!query.trim()) return;
      setSearching(true);
      setError(null);
      setResults([]);
      // Pasted coordinates (decimal or DMS) drop a pin directly — no geocoder.
      const coords = parseCoords(query.trim());
      if (coords) {
        setResults([
          {
            display_name: `Pin @ ${coords.lat.toFixed(4)} ${coords.lon.toFixed(4)}`,
            lat: coords.lat,
            lon: coords.lon,
          },
        ]);
        setSearching(false);
        return;
      }
      try {
        const res = await geocode(query.trim());
        if (res.length === 0) setError("No results — try a more specific address.");
        setResults(res);
      } catch (ex) {
        setError("Geocoding failed. Try again in a moment.");
      } finally {
        setSearching(false);
      }
    },
    [query]
  );

  const handleAdd = useCallback(
    (r) => {
      add({
        label: r.display_name.split(",")[0] || "Saved Location",
        lat: r.lat,
        lon: r.lon,
        radius_mi: 10,
      });
      setResults([]);
      setQuery("");
    },
    [add]
  );

  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Geolocation not available in this browser.");
      return;
    }
    setSearching(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lon, accuracy } = pos.coords;
        if (accuracy > 10000) {
          setError(
            `Your browser could only give a rough position (±${Math.round(accuracy / 1000)} km) — that can be hundreds of miles off. Search for your address or town instead.`,
          );
          setSearching(false);
          return;
        }
        // Coverage sanity check — network-based geolocation (satellite internet,
        // VPNs, cell hotspots) often reports the provider's gateway, not you.
        if (lat < 45.0 || lat > 50.5 || lon < -125.5 || lon > -116.5) {
          setError(
            `Your browser reports you at ${lat.toFixed(3)}, ${lon.toFixed(3)} — outside the WA/BC coverage area. This is usually your internet provider's location, not yours. Search for the address or town you want to watch instead.`,
          );
          setSearching(false);
          return;
        }
        add({
          label: "My Location",
          lat,
          lon,
          radius_mi: 10,
        });
        setSearching(false);
      },
      (err) => {
        setError(`Couldn't get your location: ${err.message}`);
        setSearching(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [add]);

  const togglePush = useCallback(async () => {
    setPushBusy(true);
    setPushMsg(null);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
        setPushMsg({ tone: "info", text: "Notifications turned off." });
      } else {
        if (locations.length === 0) {
          setPushMsg({ tone: "error", text: "Add at least one saved location first." });
          setPushBusy(false);
          return;
        }
        await subscribeToPush(locations);
        setPushEnabled(true);
        setPushMsg({
          tone: "success",
          text: "Notifications enabled. You'll be alerted when fires enter your radius.",
        });
      }
    } catch (e) {
      setPushMsg({ tone: "error", text: e.message || "Failed to update notifications." });
    } finally {
      setPushBusy(false);
    }
  }, [pushEnabled, locations]);

  const handleTestPush = useCallback(async () => {
    setPushBusy(true);
    try {
      await sendTestPush();
      setPushMsg({ tone: "success", text: "Test push sent. Check your notifications." });
    } catch (e) {
      setPushMsg({ tone: "error", text: "Test push failed. Try toggling off and on again." });
    } finally {
      setPushBusy(false);
    }
  }, []);

  if (!open) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      data-testid="saved-locations-modal"
    >
      <div
        className="w-full max-w-lg bg-tac-bg border border-tac-border rounded-sm shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-tac-border">
          <div className="flex items-center gap-2.5">
            <Star size={18} weight="fill" className="text-tac-primary" />
            <div>
              <h2 className="font-display font-black text-base text-tac-text">Saved Locations</h2>
              <p className="text-[15px] uppercase tracking-wider text-tac-faint">
                Get warned when fires get close
              </p>
            </div>
          </div>
          <button
            data-testid="close-saved-modal"
            onClick={onClose}
            className="text-tac-dim hover:text-tac-text"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div
          className="p-5 border-b border-tac-border space-y-3 overflow-y-auto tac-scroll"
          style={{ maxHeight: "40vh" }}
        >
          <LocationSearchForm
            query={query}
            setQuery={setQuery}
            searching={searching}
            onSubmit={handleSearch}
            onUseMyLocation={useMyLocation}
            inputRef={inputRef}
          />
          {error && (
            <div className="text-xs text-tac-danger px-1" data-testid="geocode-error">
              {error}
            </div>
          )}
          <GeocodeResults results={results} onSelect={handleAdd} />
        </div>

        <div className="flex-1 overflow-y-auto tac-scroll min-h-0">
          {locations.length === 0 ? (
            <div className="p-8 text-center text-[15px] text-tac-faint">
              <MapPin size={28} weight="duotone" className="mx-auto mb-3 text-tac-borderBright" />
              No saved locations yet. Add your home, cabin, or favorite trailhead and we'll warn
              you when fires come within your radius.
            </div>
          ) : (
            <ul className="divide-y divide-tac-border/60" data-testid="saved-locations-list">
              {locations.map((l) => (
                <SavedLocationItem
                  key={l.id}
                  location={l}
                  onUpdate={update}
                  onRemove={remove}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-tac-border space-y-2">
          {locations.length > 0 && (
            <button
              data-testid="save-locations-btn"
              onClick={handleSaveAll}
              disabled={saveState === "saving"}
              className={`w-full py-2 text-xs font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                saveState === "saved"
                  ? "bg-tac-safe/15 border-tac-safe text-tac-safe"
                  : saveState === "error"
                  ? "bg-tac-danger/15 border-tac-danger text-tac-danger"
                  : "bg-tac-primary border-tac-primary text-white hover:opacity-90"
              }`}
            >
              {saveState === "saving"
                ? "Saving…"
                : saveState === "saved"
                ? "✓ Locations Saved"
                : saveState === "error"
                ? "Save failed — try again"
                : "Save Locations"}
            </button>
          )}
          <PushNotificationsPanel
            pushEnabled={pushEnabled}
            pushBusy={pushBusy}
            pushMsg={pushMsg}
            onToggle={togglePush}
            onTest={handleTestPush}
          />
          <div className="text-[15px] font-mono-tac text-tac-faint flex items-center justify-between gap-2 flex-wrap">
            <span>
              Locations stored on this device. Geocoding by Google Maps (Photon fallback).
            </span>
            <SyncIndicator syncState={syncState} />
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}
